// PHASE_1 / B3 — the composed org Content (ADR-014 §2 *Content convention).
//
// Pure, shell-agnostic. The SAME component renders under the SINGLE
// parameterized AppShell in both contexts: /org (admin hat) and /me/org
// (read-only employee hat). The router mounts THIS as the routed child in both
// contexts; the server scopes the data + denies the mutations (the menu/role is
// UX only — ADR-008/ADR-017). Tabs over five sub-surfaces every caller sees —
// chart (the virtualized exit gate), diagram, headcount, reporting lines,
// teams — plus two, snapshots and scenarios, appended only for callers holding
// `hris.edit` (see SNAPSHOTS_TAB below). The create/end reporting affordances
// are convenience-gated via ScopeGate inside OrgReportingContent. No money.

import { lazy, Suspense, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Loading, PageHeader, Tab, TabList, TabPanel, Tabs } from "@novemai/components";

import {
  PageHeaderActionsProvider,
  usePageHeaderActionsHost,
} from "@/components/page-header-actions";

import { useTabParam } from "@/lib/use-tab-param";
import { useEffectivePermissions } from "@/features/roles-permissions/use-effective-permissions";
import { SCENARIO_MUTATE_CELL } from "@/features/org-scenarios/scenario-actions";

import { OrgChartContent } from "./org-chart-content";
import { OrgDiagramContent } from "./org-diagram-content";
import { OrgHeadcountContent } from "./org-headcount-content";
import { OrgReportingContent } from "./org-reporting-content";
import { OrgSnapshotsContent } from "./org-snapshots-content";
import { OrgTeamsContent } from "./org-teams-content";

export interface OrgContentProps {
  /** Convenience capability set for ScopeGate (server is the authority). */
  readonly allowedCapabilities?: readonly string[];
}

// `diagram` sits right after `chart`: they are two renderings of one dataset,
// and a reader who wants the picture looks next to the list, not at the far end
// of the strip. `chart` stays the default — it is the one that scales to 50k.
const ORG_TABS = ["chart", "diagram", "headcount", "reporting", "teams"] as const;

/**
 * `snapshots` and `scenarios` are appended only for callers who hold the cell
 * every scenario MUTATION route requires. Both read routes are only `hris.read`,
 * which a plain employee holds — and neither scope-narrows its result set, so
 * gating the tabs on the read cell would put the tenant's whole reorg pipeline
 * and its capture history in front of every employee at /me/org (the SAME
 * component renders there). Scenarios follow the gate their actions actually
 * pass; snapshots have no action of their own, they are the history that
 * scenarios capture (pre/post apply) and as-of views read, so they follow the
 * same management gate instead of the read cell.
 */
const SNAPSHOTS_TAB = "snapshots";
const SCENARIOS_TAB = "scenarios";

// Deferred like the router's own lazy routes: OrgContent is on the eager
// first-paint path (router.tsx imports it directly), and the scenario surface
// pulls in its own api + form machinery. The constant above comes from
// scenario-actions.ts by direct path rather than the barrel, so naming the cell
// does not drag the screen back into the eager graph.
const LazyOrgScenariosContent = lazy(async () => ({
  default: (await import("@/features/org-scenarios")).OrgScenariosContent,
}));

// One page header above the tab strip (org.jsx NOrg: PageHead, then tabs). The
// title and lead follow the active tab; the tab's own primary action reaches the
// header through PageHeaderActions, so the button keeps its panel-local state.
function orgHeader(
  tab: string,
  t: (key: string) => string,
): { title: ReactNode; description: string } {
  const split = (lead: string, em: string): ReactNode => (
    <>
      {lead} <em>{em}</em>
    </>
  );
  switch (tab) {
    case "diagram":
      return { title: split(t("org.title"), t("org.diagram_title_em")), description: t("org.diagram_lead") };
    case "headcount":
      return { title: split(t("org.title"), t("org.headcount_title_em")), description: t("org.headcount_lead") };
    case "reporting":
      return {
        title: split(t("org.reporting_title_lead"), t("org.reporting_title_em")),
        description: t("org.reporting_lead"),
      };
    case "teams":
      return { title: split(t("org.teams_title"), t("org.teams_title_em")), description: t("org.teams_lead") };
    case SNAPSHOTS_TAB:
      return { title: split(t("org.title"), t("org.snapshots_title_em")), description: t("org.snapshots_lead") };
    case SCENARIOS_TAB:
      return { title: t("org_scenarios.title"), description: t("org_scenarios.subtitle") };
    default:
      return { title: split(t("org.title"), t("org.chart_title_em")), description: t("org.chart_lead") };
  }
}

export function OrgContent({ allowedCapabilities = [] }: OrgContentProps): React.ReactElement {
  const { t } = useTranslation();
  const { capabilities } = useEffectivePermissions();
  const showManagementTabs = capabilities.has(SCENARIO_MUTATE_CELL);

  // The tab VALUE set is permission-derived too, not just the strip: otherwise
  // a hand-typed `?tab=scenarios` would select a tab whose panel is not
  // rendered, and the caller would get an empty page instead of the chart.
  const tabs = showManagementTabs ? [...ORG_TABS, SNAPSHOTS_TAB, SCENARIOS_TAB] : ORG_TABS;
  const [tab, setTab] = useTabParam("tab", tabs, "chart");
  const header = orgHeader(tab, t);
  const headerActions = usePageHeaderActionsHost();

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <PageHeader title={header.title} description={header.description} actions={headerActions.slot} />
      <PageHeaderActionsProvider target={headerActions.target}>
        <Tabs value={tab} onValueChange={setTab}>
          <TabList aria-label={t("org.title")}>
            <Tab value="chart">{t("org.tab.chart")}</Tab>
            <Tab value="diagram">{t("org.tab.diagram")}</Tab>
            <Tab value="headcount">{t("org.tab.headcount")}</Tab>
            <Tab value="reporting">{t("org.tab.reporting")}</Tab>
            <Tab value="teams">{t("org.tab.teams")}</Tab>
            {showManagementTabs && (
              <>
                <Tab value={SNAPSHOTS_TAB}>{t("org.tab.snapshots")}</Tab>
                <Tab value={SCENARIOS_TAB}>{t("org.tab.scenarios")}</Tab>
              </>
            )}
          </TabList>
          <TabPanel value="chart">
            <OrgChartContent />
          </TabPanel>
          <TabPanel value="diagram">
            <OrgDiagramContent />
          </TabPanel>
          <TabPanel value="headcount">
            <OrgHeadcountContent />
          </TabPanel>
          <TabPanel value="reporting">
            <OrgReportingContent allowedCapabilities={allowedCapabilities} />
          </TabPanel>
          <TabPanel value="teams">
            <OrgTeamsContent />
          </TabPanel>
          {showManagementTabs && (
            <>
              <TabPanel value={SNAPSHOTS_TAB}>
                <OrgSnapshotsContent />
              </TabPanel>
              <TabPanel value={SCENARIOS_TAB}>
                <Suspense fallback={<Loading label={t("org_scenarios.loading")} rows={4} />}>
                  <LazyOrgScenariosContent />
                </Suspense>
              </TabPanel>
            </>
          )}
        </Tabs>
      </PageHeaderActionsProvider>
    </div>
  );
}
